import React from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  PageHeader,
  SectionHeader,
} from "../ui";

const setupSteps = [
  {
    title: "Crear torneo",
    description:
      "Entra en Crear torneo, completa nombre y fecha/hora de inicio y guarda. La app te lleva al setup automaticamente.",
    route: "/crear-torneo",
    routeLabel: "Ir a Crear torneo",
  },
  {
    title: "Configurar grupos y equipos",
    description:
      "En Setup > Grupos y equipos: crea grupos, da de alta equipos en la bolsa y asignalos a cada grupo.",
  },
  {
    title: "Montar calendario de grupos",
    description:
      "En Setup > Calendario grupo puedes crear partidos manuales o generar round-robin automatico con ida/vuelta.",
  },
  {
    title: "Preparar eliminatoria",
    description:
      "En Setup > Eliminatoria crea cruces manuales o genera cuadro automatico (4/8/16), y conecta origen-destino con orden.",
  },
  {
    title: "Invitar colaboradores",
    description:
      "Desde Colaboradores invita por email como Admin o LiveOperator para repartir tareas.",
  },
  {
    title: "Operar en vivo",
    description:
      "Desde Mesa (movil) abre partidos, marca goles, finaliza o reabre para corregir.",
  },
  {
    title: "Publicar para el publico",
    description:
      "En Setup > Portada y publico selecciona Privado, Solo enlace o Publico y guarda visibilidad.",
  },
];

const visibilityModes = [
  {
    title: "Privado",
    description: "Solo usuarios con acceso en la app (organizacion y personas invitadas).",
  },
  {
    title: "Solo enlace",
    description: "Genera un codigo (shareCode) para compartir sin listar en portada.",
  },
  {
    title: "Publico",
    description: "Aparece en portada publica. Requiere slug unico y que no este finalizado.",
  },
];

const setupTabs = [
  {
    title: "Grupos y equipos",
    points: [
      "Crear grupo nuevo",
      "Crear equipo en bolsa",
      "Crear y anadir equipo directamente a un grupo",
      "Mover equipos desde bolsa al grupo",
      "Eliminar equipo del grupo o borrar grupo completo",
    ],
  },
  {
    title: "Solo equipos",
    points: [
      "Gestion rapida de la bolsa de equipos",
      "Alta de equipos sin tocar grupos",
      "Ideal para cargar participantes antes de repartir",
    ],
  },
  {
    title: "Calendario grupo",
    points: [
      "Anadir partido manual (local, visitante, fecha y campo)",
      "Editar o borrar partidos de grupo",
      "Generar round-robin automatico",
      "Activar ida y vuelta",
      "Definir minutos entre partidos",
    ],
  },
  {
    title: "Eliminatoria",
    points: [
      "Crear partido manual con equipos o plazas de grupo",
      "Conectar origen -> destino con orden (1 local, 2 visitante)",
      "Editar nombre, horario, campo y conexiones",
      "Generar cuadro automatico de 4, 8 o 16 clasificados",
      "Eliminar partidos o enlaces si cambias el diseno",
    ],
  },
];

const tournamentActionRoutes = [
  { title: "Mis torneos", path: "/mis-torneos", note: "Torneos donde eres propietario, admin u operador en vivo." },
  { title: "Mis favoritos", path: "/mis-favoritos", note: "Torneos que marcas con Seguir torneo; solo aparecen si aún puedes verlos (miembro o público)." },
  {
    title: "Acciones del torneo",
    path: "/tournamentactions/:tournamentId",
    note: "Panel principal del torneo (gestión y vistas públicas). El parámetro puede ser el GUID, el slug público o el código de enlace (solo enlace).",
  },
  {
    title: "Setup",
    path: "/tournamentactions/:tournamentId/setup",
    note: "Configuracion completa del torneo.",
  },
  {
    title: "Colaboradores",
    path: "/tournamentactions/:tournamentId/colaboradores",
    note: "Invitaciones por email con rol Admin o LiveOperator.",
  },
  {
    title: "Desempate manual",
    path: "/tournamentactions/:tournamentId/orden-grupos",
    note: "Ajuste manual del orden de grupo cuando hace falta criterio extra.",
  },
  {
    title: "Mesa",
    path: "/tournamentactions/:tournamentId/mesa",
    note: "Operacion de marcador para partidos de grupo y eliminatoria.",
  },
  { title: "Vista grupos", path: "/viewgroups/:tournamentId", note: "Tabla de grupos para publico y staff." },
  { title: "Vista partidos", path: "/matches/:tournamentId", note: "Calendario por campos." },
  { title: "Vista eliminatoria", path: "/viewbracket/:tournamentId", note: "Cuadro de fase final." },
  { title: "Modo visor", path: "/viewer/:tournamentId", note: "Pantalla TV para evento en sala." },
];

const roleCards = [
  {
    title: "Owner",
    description:
      "Creador del torneo. Control total: setup, publicacion, colaboradores, mesa y vistas.",
    variant: "gold",
  },
  {
    title: "Admin",
    description:
      "Gestion completa del torneo y de su configuracion. Puede operar mesa y ajustar estructura.",
    variant: "info",
  },
  {
    title: "LiveOperator",
    description:
      "Rol de operacion en vivo para marcador. Entra a Mesa y gestiona resultados durante el evento.",
    variant: "success",
  },
];

const faqItems = [
  {
    q: "No aparece en portada aunque esta en Publico",
    a: "Revisa que el torneo no este marcado como finalizado y que el slug sea valido y unico.",
  },
  {
    q: "No puedo invitar colaboradores",
    a: "Solo Owner/Admin pueden gestionar miembros. La cuenta invitada debe existir en la app.",
  },
  {
    q: "No deja abrir un partido de eliminatoria en mesa",
    a: "Ese cruce necesita tener ambos lados definidos (equipos o clasificados) para poder operarlo.",
  },
  {
    q: "Quiero compartir sin listar en inicio",
    a: "Usa visibilidad Solo enlace y comparte la URL con el shareCode configurado.",
  },
];

export default function DocumentationPage() {
  return (
    <div className="ui-page-wrap py-4 sm:py-6">
      <PageHeader
        title="Documentación de uso de Torneos Club Evolver"
        subtitle="Guía práctica para crear, configurar, operar y publicar torneos paso a paso."
        actions={
          <div className="flex flex-wrap gap-2">
            <Button as={RouterLink} to="/crear-torneo" variant="gold">
              Crear torneo
            </Button>
            <Button as={RouterLink} to="/" variant="outline">
              Ver portada
            </Button>
            <Button as={RouterLink} to="/mis-torneos" variant="outline">
              Ir a mis torneos
            </Button>
          </div>
        }
      />

      <section className="mt-8" id="indice">
        <SectionHeader
          title="Indice rapido"
          subtitle="Accede al bloque que necesitas ahora."
        />
        <Card>
          <CardBody className="flex flex-wrap gap-2">
            <Button as="a" href="#flujo-recomendado" variant="outline" size="sm">
              Flujo recomendado
            </Button>
            <Button as="a" href="#acciones" variant="outline" size="sm">
              Acciones del torneo
            </Button>
            <Button as="a" href="#setup" variant="outline" size="sm">
              Setup por pestanas
            </Button>
            <Button as="a" href="#publicacion" variant="outline" size="sm">
              Visibilidad y publicacion
            </Button>
            <Button as="a" href="#roles" variant="outline" size="sm">
              Roles y permisos
            </Button>
            <Button as="a" href="#faq" variant="outline" size="sm">
              Preguntas frecuentes
            </Button>
          </CardBody>
        </Card>
      </section>

      <section className="mt-10" id="flujo-recomendado">
        <SectionHeader
          title="Flujo recomendado"
          subtitle="Si es tu primera vez, sigue estos pasos para montar un torneo de principio a fin."
        />
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {setupSteps.map((step, index) => (
            <Card key={step.title}>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Badge variant="gold">Paso {index + 1}</Badge>
                  <h2 className="font-display text-xl font-extrabold text-ink">{step.title}</h2>
                </div>
              </CardHeader>
              <CardBody>
                <p className="text-sm leading-relaxed text-ink-muted">{step.description}</p>
                {step.route ? (
                  <div className="mt-4">
                    <Button as={RouterLink} to={step.route} size="sm" variant="outline">
                      {step.routeLabel}
                    </Button>
                  </div>
                ) : null}
              </CardBody>
            </Card>
          ))}
        </div>
      </section>

      <section className="mt-10" id="acciones">
        <SectionHeader
          title="Acciones del torneo"
          subtitle="Rutas y pantallas clave para operar durante toda la vida del torneo."
        />
        <div className="grid gap-4 md:grid-cols-2">
          {tournamentActionRoutes.map((item) => (
            <Card key={item.path}>
              <CardHeader>
                <h3 className="font-display text-lg font-extrabold text-ink">{item.title}</h3>
              </CardHeader>
              <CardBody className="space-y-2">
                <p className="rounded-lg bg-cream-deep px-3 py-2 font-mono text-xs text-ink-soft">{item.path}</p>
                <p className="text-sm text-ink-muted">{item.note}</p>
                {item.path === "/mis-torneos" || item.path === "/mis-favoritos" ? (
                  <Button as={RouterLink} to={item.path} size="sm" variant="outline">
                    Abrir
                  </Button>
                ) : null}
              </CardBody>
            </Card>
          ))}
        </div>
      </section>

      <section className="mt-10" id="setup">
        <SectionHeader
          title="Setup por pestanas"
          subtitle="Todo lo que puedes hacer dentro de Configurar torneo."
        />
        <div className="grid gap-4 md:grid-cols-2">
          {setupTabs.map((tab) => (
            <Card key={tab.title}>
              <CardHeader>
                <h3 className="font-display text-lg font-extrabold text-ink">{tab.title}</h3>
              </CardHeader>
              <CardBody>
                <ul className="list-disc space-y-1 pl-5 text-sm text-ink-muted">
                  {tab.points.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </CardBody>
            </Card>
          ))}
        </div>
      </section>

      <section className="mt-10" id="publicacion">
        <SectionHeader
          title="Visibilidad y publicacion"
          subtitle="Bloque clave para mostrar el torneo en portada o compartirlo por enlace."
        />
        <Card>
          <CardBody className="space-y-3">
            <p className="text-sm text-ink-muted">
              Ve a <strong>Acciones del torneo</strong> -&gt; <strong>Configurar torneo</strong> y abre el bloque{" "}
              <strong>Portada y publico</strong>.
            </p>
            <ol className="list-decimal space-y-2 pl-5 text-sm text-ink-muted">
              <li>Elige una visibilidad: Privado, Solo enlace o Publico.</li>
              <li>Si eliges Publico, completa un slug unico (ejemplo: copa-verano-2026).</li>
              <li>Si eliges Solo enlace, define shareCode para generar el acceso compartible.</li>
              <li>Pulsa Guardar visibilidad.</li>
              <li>Si quieres salir en portada, el torneo no debe estar finalizado.</li>
            </ol>
          </CardBody>
        </Card>
        <div className="mt-4 grid gap-4 md:grid-cols-3">
          {visibilityModes.map((mode) => (
            <Card key={mode.title}>
              <CardHeader>
                <h3 className="font-display text-lg font-extrabold text-ink">{mode.title}</h3>
              </CardHeader>
              <CardBody>
                <p className="text-sm leading-relaxed text-ink-muted">{mode.description}</p>
              </CardBody>
            </Card>
          ))}
        </div>
      </section>

      <section className="mt-10" id="roles">
        <SectionHeader
          title="Roles y permisos"
          subtitle="Que puede hacer cada perfil dentro del torneo."
        />
        <div className="grid gap-4 md:grid-cols-3">
          {roleCards.map((role) => (
            <Card key={role.title}>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Badge variant={role.variant}>{role.title}</Badge>
                </div>
              </CardHeader>
              <CardBody>
                <p className="text-sm text-ink-muted">{role.description}</p>
              </CardBody>
            </Card>
          ))}
        </div>
      </section>

      <section className="mt-10" id="faq">
        <SectionHeader
          title="Preguntas frecuentes"
          subtitle="Problemas comunes y como resolverlos rapido."
        />
        <div className="grid gap-4 md:grid-cols-2">
          {faqItems.map((item) => (
            <Card key={item.q}>
              <CardHeader>
                <h3 className="font-display text-lg font-extrabold text-ink">{item.q}</h3>
              </CardHeader>
              <CardBody>
                <p className="text-sm leading-relaxed text-ink-muted">{item.a}</p>
              </CardBody>
            </Card>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <Card>
          <CardHeader>
            <h3 className="font-display text-xl font-extrabold text-ink">Si quieres un portal de docs separado</h3>
          </CardHeader>
          <CardBody className="space-y-3 text-sm text-ink-muted">
            <p>
              Si quieres una documentacion tecnica externa (versionada y con buscador), opciones comunes son{" "}
              <strong>Docusaurus</strong>, <strong>Nextra</strong> o <strong>MkDocs</strong>.
            </p>
            <p>
              Para usuarios finales, esta guia integrada en la app suele funcionar mejor porque resuelve dudas justo
              en el momento de uso.
            </p>
            <p>
              Si mas adelante quieres, se puede montar ese portal y enlazarlo desde esta misma pantalla.
            </p>
          </CardBody>
        </Card>
      </section>
    </div>
  );
}
